import { createSelector } from 'reselect';

import {
  DomainTables,
  GuideSADTForm,
  HiredSolicitantAutocompleteEntry,
  HiredSolicitantSlug,
  ProcedureAutocompleteEntry,
  ProfessionCodeAutocompleteEntry,
} from 'features/tissInvoicing/types';
import { StoreState } from 'state/rootReducer';

export const termCode = '‏‏‎ ‎';

export const blankGenericOptions = { id: 0, term: termCode };
export const blankProcedureOption = {
  id: 0,
  name: '‎‎‎‏‏‎ ‎',
  price: '0.00',
  tuss_procedure_id: null,
};

export const blankSessionOption = {
  sessionOrder: null,
  sessionDay: null,
  sessionDate: '',
};

export const formatDomainTablesOptions = ({
  access_ways,
  accident_indication,
  appointment_types,
  councils,
  degrees_participation,
  profession_code,
  reasons_termination,
  service_character,
  service_types,
  states,
  techniques_used,
  attendance_regimen,
  occupational_health,
  special_coverages,
}: DomainTables) => ({
  techniques: [{ ...blankGenericOptions }, ...techniques_used],
  accessWays: [{ ...blankGenericOptions }, ...access_ways],
  states: [{ ...blankGenericOptions, term_short: termCode }, ...states],
  councils: [{ ...blankGenericOptions, term_short: termCode }, ...councils],
  appointmentTypes: [{ ...blankGenericOptions }, ...appointment_types],
  accidentIndications: [{ ...blankGenericOptions }, ...accident_indication],
  serviceTypes: [{ ...blankGenericOptions }, ...service_types],
  serviceCharacters: [{ ...blankGenericOptions }, ...service_character],
  professionCodes: [{ ...blankGenericOptions }, ...profession_code],
  participationDegrees: [{ ...blankGenericOptions }, ...degrees_participation],
  terminationReasons: [{ ...blankGenericOptions }, ...reasons_termination],
  attendanceRegimens: [{ ...blankGenericOptions }, ...attendance_regimen],
  occupationalHealthList: [{ ...blankGenericOptions }, ...occupational_health],
  specialCoverages: [{ ...blankGenericOptions }, ...special_coverages],
});

export const getGuideDataState = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.guide;

export const getGuideState = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.lot.listGuides.availableGuides;

export const getLoadingState = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.loading;

export const getSnackbarProps = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.snackbar;

export const getSadtFormInitalData = ({
  tissInvoicing,
}: StoreState): GuideSADTForm => tissInvoicing.sadt.form.data;

export const getPrintState = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.print;

export const getDomainTablesOptions = ({ tissInvoicing }: StoreState) => {
  const {
    sadt: { domainTables },
  } = tissInvoicing;

  return formatDomainTablesOptions(domainTables);
};

export const getProcedures = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.autocomplete?.procedures;

export const getPhysicians = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.autocomplete?.physicians;

export const getClinics = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.autocomplete?.clinics;

export const getAutocomplete = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.autocomplete;

export const getHiredOptions = createSelector(
  getPhysicians,
  getClinics,
  (physicians, clinics): HiredSolicitantAutocompleteEntry[] => [
    ...physicians.map((p) => ({
      ...p,
      type: 'Profissionais',
      slug: HiredSolicitantSlug.Physician,
    })),
    ...clinics.map((c) => ({
      ...c,
      type: 'Clínicas',
      slug: HiredSolicitantSlug.Clinic,
    })),
  ],
);

export const getProcedureOptions = createSelector(
  getProcedures,
  (procedures): ProcedureAutocompleteEntry[] => [
    { ...blankProcedureOption },
    ...procedures,
  ],
);

export const getProfessionCodes = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.domainTables?.profession_code;

export const getCboOptions = createSelector(
  getProfessionCodes,
  (profession_code): ProfessionCodeAutocompleteEntry[] => [
    ...profession_code.map((p) => ({
      ...p,
      name: `${p.code} - ${p.term}`,
      type: 'cbo',
    })),
  ],
);

export const getExpensesErrors = ({ tissInvoicing }: StoreState) =>
  tissInvoicing.sadt.form.expensesErrors;
